<script setup lang="ts">
/**
 * Экран «Меняйте на свой вкус» для одного слота комбо.
 *
 * Вёрстка модификаторов не своя: переиспользуем ProductAddons/ProductExcludes —
 * те же карточки с ценой и чипсы «Убрать ингредиенты», что в карточке товара.
 * На большом экране блок шире, поэтому карточки крупнее (см. стили ниже).
 *
 * Выбор уходит на бэкенд внутри combo_selections[].modifiers —
 * цену и допустимость добавки проверяет сервер.
 */

import { computed } from 'vue';
import type { ProductAddon, ProductExclude } from '@entities/product';
import ProductAddons from '~/entities/product/ui/ProductAddons.vue';
import ProductExcludes from '~/entities/product/ui/ProductExcludes.vue';
import type { ComboSlot } from '@features/combo';

const props = defineProps<{
  comboSlot: ComboSlot;
}>();

const emit = defineEmits<{
  (e: 'updateAddons', addonIds: number[]): void;
  (e: 'updateExcludes', excludeIds: number[]): void;
  (e: 'reset'): void;
  (e: 'close'): void;
}>();

/** Модель слота хранит готовый URL, а компоненты товара ждут объект img */
const addons = computed<ProductAddon[]>(() =>
  props.comboSlot.addons.map((addon) => ({
    id: addon.id,
    name: addon.name,
    price: addon.price,
    img: addon.img ? { url: addon.img } : undefined,
  })),
);

const excludes = computed<ProductExclude[]>(() =>
  props.comboSlot.excludes.map((exclude) => ({
    id: exclude.id,
    name: exclude.name,
    img: exclude.img ? { url: exclude.img } : undefined,
  })),
);

const hasCustomization = computed(
  () => props.comboSlot.selectedAddons.length > 0 || props.comboSlot.selectedExcludes.length > 0,
);
</script>

<template>
  <div class="combo-customize" data-testid="combo-customize-panel">
    <div class="combo-customize__header">
      <button type="button" class="combo-customize__back" aria-label="Назад" @click="emit('close')">
        <UIcon name="i-heroicons-chevron-left" class="w-6 h-6" />
      </button>
      <div>
        <h3 class="combo-customize__title">Меняйте на свой вкус</h3>
        <p class="combo-customize__subtitle">{{ comboSlot.product?.name }}</p>
      </div>
    </div>

    <div class="combo-customize__content">
      <ProductAddons
        :addons="addons"
        :model-value="comboSlot.selectedAddons"
        is-mobile
        class="combo-customize__addons"
        @update:model-value="emit('updateAddons', $event)"
      />

      <ProductExcludes
        :excludes="excludes"
        :model-value="comboSlot.selectedExcludes"
        is-mobile
        class="combo-customize__excludes"
        @update:model-value="emit('updateExcludes', $event)"
      />
    </div>

    <div class="combo-customize__footer">
      <button
        type="button"
        class="combo-customize__reset"
        :disabled="!hasCustomization"
        @click="emit('reset')"
      >
        Сбросить
      </button>
      <button type="button" class="combo-customize__done" @click="emit('close')">Готово</button>
    </div>
  </div>
</template>

<style scoped lang="scss">
.combo-customize {
  @apply relative h-full flex flex-col min-h-0;

  // Матовое стекло: сквозь панель просвечивает фото блюда.
  // Блюр — на ::before, а не на самом контейнере: в WebKit backdrop-filter
  // на flex-контейнере ломает расчёт высоты детей (те же грабли, что в
  // more-about-dish__content). Фон тёмный — карточки добавок рассчитаны
  // на тёмную подложку (bg-gray-700/40 + белый текст).
  &::before {
    content: '';
    position: absolute;
    inset: 0;
    z-index: 0;
    pointer-events: none;
    background-color: rgba(18, 18, 20, 0.72);
    backdrop-filter: blur(28px) saturate(1.4);
    -webkit-backdrop-filter: blur(28px) saturate(1.4);
  }

  & > * {
    position: relative;
    z-index: 1;
  }

  &__header {
    @apply flex items-center gap-3 px-4 pt-4 pb-3 shrink-0;
  }

  &__back {
    @apply w-9 h-9 rounded-full flex items-center justify-center shrink-0;
    @apply bg-white/10 text-white hover:bg-white/20 transition-colors;
  }

  &__title {
    @apply text-lg lg:text-2xl font-bold text-white leading-tight;
  }

  &__subtitle {
    @apply text-xs lg:text-sm text-gray-400;
  }

  &__content {
    @apply flex-1 min-h-0 overflow-y-auto overflow-x-hidden px-4 pb-4;
    @apply flex flex-col gap-6;
  }

  &__addons,
  &__excludes {
    @apply shrink-0;
  }

  // Заголовки компонентов белеют только на узких экранах (там sheet всегда тёмный).
  // Панель комбо тёмная на любой ширине и в любой теме — иначе на светлой теме
  // «Добавить по вкусу» и «Убрать ингредиенты» сливались с фоном.
  :deep(.product-addons__title),
  :deep(.product-excludes__title) {
    @apply text-white;
  }

  // ── Адаптация под большой экран ──────────────────────────────────────────
  // Компоненты рассчитаны на узкий bottom sheet; в колонке модалки места больше,
  // поэтому увеличиваем карточки и картинки, оставляя ту же сетку 4 в ряд.
  @screen lg {
    :deep(.product-addons__grid) {
      @apply gap-3;
    }

    :deep(.product-addons__card) {
      @apply p-3 rounded-xl;
    }

    :deep(.product-addons__card-image-wrapper) {
      @apply h-24 mb-3;
    }

    :deep(.product-addons__card-name) {
      @apply text-sm;
    }

    :deep(.product-addons__card-price) {
      @apply text-sm font-semibold;
    }

    :deep(.product-addons__title),
    :deep(.product-excludes__title) {
      @apply text-lg;
    }

    :deep(.product-excludes__tag) {
      @apply px-4 py-2.5;
    }
  }

  &__footer {
    @apply flex items-center gap-2 p-4 shrink-0;
  }

  &__reset {
    @apply px-4 py-3 rounded-full text-sm font-medium;
    @apply bg-white/10 text-gray-200 hover:bg-white/20 transition-colors;
    @apply disabled:opacity-40 disabled:cursor-not-allowed;
  }

  &__done {
    @apply flex-1 py-3 rounded-full font-semibold text-white bg-red-500;
    @apply hover:bg-red-600 transition-colors;
  }
}
</style>
